import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../components/layout';
import KnowledgeBaseForm from '../../components/knowledge-bases/KnowledgeBaseForm';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiService } from '../../services/apiService';
import type {
    KnowledgeBaseFormData
} from "../../types/knowledge-base";
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

const validateKnowledgeBaseForm = (formData: KnowledgeBaseFormData): string[] => {
    const errors: string[] = [];

    if (!formData.name.trim()) {
        errors.push('Name is required');
    }

    if (!formData.sourceType) {
        errors.push('Source type is required');
    }

    if (!formData.embeddingModel) {
        errors.push('Embedding model is required');
    }

    if (formData.sourceType !== 'apis') {
        if (!formData.chunkSize || formData.chunkSize < 100 || formData.chunkSize > 2000) {
            errors.push('Chunk size must be between 100 and 2000');
        }

        if (formData.overlap < 0 || formData.overlap > 200) {
            errors.push('Overlap must be between 0 and 200');
        }
    }

    if (!formData.dataSourceIds?.length) {
        errors.push(`At least one ${formData.sourceType === 'files' ? 'file' :
            formData.sourceType === 'websites' ? 'website' :
                formData.sourceType === 'apis' ? 'API endpoint' :
                    'data source'} must be selected`);
    }

    return errors;
};

const CreateKnowledgeBase: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const modalState = useModal();
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const [formData, setFormData] = useState<KnowledgeBaseFormData>({
        name: '',
        description: '',
        sourceType: '',
        chunkSize: 500,
        overlap: 50,
        embeddingModel: 'text-embedding-ada-002',
        dataSourceIds: []
    });

    // Effect to handle clone data
    useEffect(() => {
        const initializeForm = async () => {
            setIsInitialLoading(true);
            try {
                const cloneData = location.state?.cloneData;
                if (cloneData) {
                    setFormData(prevData => ({
                        ...prevData,
                        ...cloneData,
                        // Ensure name ends with (Clone) if coming from clone operation
                        name: cloneData.name.endsWith('(Clone)')
                            ? cloneData.name
                            : `${cloneData.name} (Clone)`
                    }));
                }
            } catch (err) {
                console.error('Error initializing form:', err);
                modalState.setError(err instanceof Error ? err.message : 'An error occurred');
            } finally {
                setIsInitialLoading(false);
            }
        };

        initializeForm();
    }, [location.state]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        modalState.setIsLoading(true);
        modalState.setError(null);

        try {
            const validationErrors = validateKnowledgeBaseForm(formData);
            if (validationErrors.length > 0) {
                throw new Error(validationErrors.join('\n'));
            }

            const indexPayload = {
                name: formData.name.trim(),
                description: formData.description?.trim(),
                source_type: formData.sourceType,
                chunk_size: formData.chunkSize,
                overlap: formData.overlap,
                embedding_model: formData.embeddingModel,
                data_source_ids: formData.dataSourceIds
            };

            console.log('Creating index with payload:', JSON.stringify(indexPayload, null, 2));

            const indexResult = await apiService.post(API_ENDPOINTS.INDEXES.CREATE, indexPayload);
            console.log('Index created successfully:', indexResult);

            const message = location.state?.cloneData
                ? 'Knowledge Base cloned successfully'
                : 'Knowledge Base created successfully';

            modalState.setSuccess(message);
            navigate('/knowledge-bases');
        } catch (err) {
            console.error('Error creating knowledge base:', err);
            modalState.setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            modalState.setIsLoading(false);
        }
    };

    const handleFieldChange = (
        field: keyof KnowledgeBaseFormData,
        value: KnowledgeBaseFormData[keyof KnowledgeBaseFormData]
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    if (!user) return null;

    return (
        <div className="page-container">
            <div className="content-header">
                <h5 className="mb-4 fw-semibold">
                    {location.state?.cloneData ? 'Clone Knowledge Base' : 'New Knowledge Base'}
                </h5>
                {(isInitialLoading || modalState.isLoading) ? (
                    <ContentSkeleton />
                ) : (
                    <KnowledgeBaseForm
                        formData={formData}
                        onSubmit={handleSubmit}
                        onChange={handleFieldChange}
                        error={modalState.error}
                        isLoading={modalState.isLoading}
                        mode="create"
                        isClone={Boolean(location.state?.cloneData)}
                    />
                )}
            </div>
        </div>
    );
};

export default CreateKnowledgeBase;